<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import type {
  AudienceType,
  ProviderCode,
  PushApp,
  PushTask,
  PushTaskDetail,
  PushTemplate
} from './types'
import { AUDIENCE_TYPES, pushAppOptions, PUSH_PROVIDERS } from './types'
import { formatTime, providerLabel } from './usePushCenterState'
import { Plus } from '@lucide/vue'
import {
  AdminDataTable,
  AdminDialog,
  AdminFilterBar,
  AdminStatusTag,
  RrButton,
  RrInput,
  RrSelect,
  RrTag,
  type TableColumnType
} from '@xjlv2/admin-layout'

const props = withDefaults(
  defineProps<{
    tasks?: PushTask[]
    keyword: string
    total: number
    page: number
    pageSize: number
    queryLoading?: boolean
    templates?: PushTemplate[]
    apps?: PushApp[]
    appCode?: string
    canManage?: boolean
    createApiClient: () => ApiClient
    cancelTask: (taskId: number) => Promise<void>
    retryTask: (taskId: number) => Promise<void>
    loadTaskDetail: (taskId: number) => Promise<PushTaskDetail>
  }>(),
  {
    tasks: () => [],
    queryLoading: false,
    templates: () => [],
    apps: () => [],
    appCode: '',
    canManage: false
  }
)

const emit = defineEmits<{
  'update:appCode': [code: string]
  search: [keyword: string, appCode: string]
  'page-change': [page: number, pageSize: number]
  refresh: []
  notice: [msg: string]
  error: [msg: string]
}>()

const localAppCode = ref(props.appCode)
watch(
  () => props.appCode,
  (code) => {
    localAppCode.value = code
  }
)

function onAppChange(code: string) {
  emit('update:appCode', code)
}

const keyword = ref(props.keyword)
watch(
  () => props.keyword,
  (value) => {
    keyword.value = value
  }
)
const dialogVisible = ref(false)
const loading = ref(false)
const actingTaskId = ref<number | null>(null)
const detailVisible = ref(false)
const detailLoading = ref(false)
const taskDetail = ref<PushTaskDetail | null>(null)

function canCancel(task: PushTask) {
  return task.status === 'PENDING' || task.status === 'SCHEDULED' || task.status === 'SENDING'
}

function canRetry(task: PushTask) {
  return task.status === 'FAILED' && task.failureCount > 0
}

async function openDetail(task: PushTask) {
  detailLoading.value = true
  detailVisible.value = true
  try {
    taskDetail.value = await props.loadTaskDetail(task.id)
  } catch (err) {
    detailVisible.value = false
    emit('error', err instanceof Error ? err.message : '加载任务详情失败')
  } finally {
    detailLoading.value = false
  }
}

async function handleCancel(task: PushTask) {
  if (!props.canManage || !canCancel(task) || actingTaskId.value !== null) return
  if (!window.confirm(`确定取消任务「${task.title}」吗？已提交的外部尝试不会被召回。`)) {
    return
  }
  actingTaskId.value = task.id
  try {
    await props.cancelTask(task.id)
    emit('refresh')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '任务取消失败')
  } finally {
    actingTaskId.value = null
  }
}

async function handleRetry(task: PushTask) {
  if (!props.canManage || !canRetry(task) || actingTaskId.value !== null) return
  if (!window.confirm(`确定重试任务「${task.title}」的确定失败投递吗？未知结果不会自动重发。`)) {
    return
  }
  actingTaskId.value = task.id
  try {
    await props.retryTask(task.id)
    emit('refresh')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '任务重试失败')
  } finally {
    actingTaskId.value = null
  }
}

const columns: TableColumnType<PushTask>[] = [
  { key: 'id', title: 'ID', width: 80 },
  { key: 'appCode', title: '应用', width: 120 },
  { key: 'provider', title: '通道', width: 130 },
  { key: 'title', title: '任务标题', minWidth: 200 },
  { key: 'audience', title: '受众与总数', width: 150 },
  { key: 'progress', title: '成功 / 失败', width: 140 },
  { key: 'status', title: '状态', width: 100 },
  { key: 'createdAt', title: '创建时间', width: 180 },
  { key: 'actions', title: '操作', width: 210 }
]

const form = ref({
  appCode: props.appCode,
  provider: 'JPUSH' as ProviderCode,
  templateCode: '',
  templateParams: '{}',
  title: '',
  body: '',
  audienceType: 'ALL' as AudienceType,
  userIds: '',
  deviceIds: '',
  tags: '',
  data: '{}'
})

const activeTemplates = computed(() => (props.templates || []).filter((t) => t.enabled))

function handleSearch() {
  emit('search', keyword.value.trim(), localAppCode.value)
}

function handleReset() {
  keyword.value = ''
  localAppCode.value = ''
  emit('search', '', '')
}

function openDialog() {
  form.value = {
    appCode: localAppCode.value || props.appCode,
    provider: 'JPUSH',
    templateCode: '',
    templateParams: '{}',
    title: '',
    body: '',
    audienceType: 'ALL',
    userIds: '',
    deviceIds: '',
    tags: '',
    data: '{}'
  }
  dialogVisible.value = true
}

function parseIdList(value: string, fieldName: string) {
  if (!value.trim()) return []
  return value
    .split(/[\s,，]+/)
    .filter(Boolean)
    .map((item) => {
      if (!/^\d+$/.test(item) || item === '0') {
        throw new Error(`${fieldName} 必须是用逗号分隔的正整数`)
      }
      return Number(item)
    })
}

function parseTags(value: string) {
  return value
    .split(/[\s,，]+/)
    .map((item) => item.trim())
    .filter(Boolean)
}

async function createTask() {
  if (!props.canManage) return
  loading.value = true
  try {
    const api = props.createApiClient()
    const payload = {
      ...form.value,
      userIds: parseIdList(form.value.userIds, '用户 ID'),
      deviceIds: parseIdList(form.value.deviceIds, '设备 ID'),
      tags: parseTags(form.value.tags),
      templateParams: JSON.parse(form.value.templateParams || '{}'),
      data: JSON.parse(form.value.data || '{}'),
      idempotencyKey: crypto.randomUUID()
    }
    await api.post('/api/saas/push/tasks', payload)
    emit('notice', `推送任务 "${form.value.title}" 已创建`)
    dialogVisible.value = false
    emit('refresh')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '推送任务创建失败')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="push-tab-pane">
    <AdminFilterBar :loading="loading || queryLoading" @submit="handleSearch" @reset="handleReset">
      <label>
        所属应用
        <RrSelect
          v-model:value="localAppCode"
          :options="pushAppOptions(apps)"
          @change="onAppChange"
        />
      </label>
      <label>
        关键字
        <RrInput
          v-model:value="keyword"
          placeholder="输入任务标题 / 通道进行搜索"
          @keyup.enter="handleSearch"
        />
      </label>
      <template #actions>
        <RrButton type="primary" :disabled="!canManage" @click="openDialog()">
          <template #icon><Plus :size="16" /></template>
          创建推送任务
        </RrButton>
      </template>
    </AdminFilterBar>

    <AdminDataTable
      :columns="columns"
      :data-source="tasks"
      :loading="loading || queryLoading"
      :current="page"
      :page-size="pageSize"
      :total="total"
      :empty="tasks.length === 0"
      empty-title="暂无推送任务"
      empty-description="可点击上方「创建推送任务」按钮发起批量推送。"
      @update:current="(p) => emit('page-change', p, pageSize)"
      @update:page-size="
        (size) => {
          emit('page-change', 1, size)
        }
      "
      @refresh="emit('refresh')"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'id'">
          <code>#{{ record.id }}</code>
        </template>
        <template v-else-if="column.key === 'appCode'">
          <strong>{{ record.appCode }}</strong>
        </template>
        <template v-else-if="column.key === 'provider'">
          <RrTag tone="primary" :label="providerLabel(record.provider)" />
        </template>
        <template v-else-if="column.key === 'title'">
          <strong>{{ record.title }}</strong>
        </template>
        <template v-else-if="column.key === 'audience'">
          {{ record.audienceType }} ({{ record.totalCount }}人)
        </template>
        <template v-else-if="column.key === 'progress'">
          <span class="text-green">{{ record.successCount }} 成功</span> /
          <span class="text-red">{{ record.failureCount }} 失败</span> /
          <span>{{ record.skippedCount }} 跳过</span>
        </template>
        <template v-else-if="column.key === 'status'">
          <AdminStatusTag
            :text="record.status"
            :tone="record.status === 'SENT' ? 'success' : 'neutral'"
          />
        </template>
        <template v-else-if="column.key === 'createdAt'">
          <span class="text-time">{{ formatTime(record.createdAt) }}</span>
        </template>
        <template v-else-if="column.key === 'actions'">
          <div class="row-actions">
            <RrButton html-type="button" size="small" @click="openDetail(record)">详情</RrButton>
            <RrButton
              v-if="canManage && canCancel(record)"
              html-type="button"
              size="small"
              danger
              :loading="actingTaskId === record.id"
              @click="handleCancel(record)"
            >
              取消
            </RrButton>
            <RrButton
              v-if="canManage && canRetry(record)"
              html-type="button"
              size="small"
              :loading="actingTaskId === record.id"
              @click="handleRetry(record)"
            >
              重试
            </RrButton>
          </div>
        </template>
      </template>
    </AdminDataTable>

    <!-- 创建任务标准弹窗 -->
    <AdminDialog
      v-model:open="dialogVisible"
      title="创建推送任务"
      :width="640"
      :confirm-loading="loading"
      ok-text="立即创建"
      @ok="createTask"
      @cancel="dialogVisible = false"
    >
      <div class="modal-form-grid">
        <div class="form-row-2">
          <label class="form-field">
            <span class="field-label required">应用编码</span>
            <RrInput
              v-model:value.trim="form.appCode"
              placeholder="请输入应用编码"
              autocomplete="off"
            />
          </label>
          <label class="form-field">
            <span class="field-label required">推送通道</span>
            <RrSelect
              v-model:value="form.provider"
              :options="
                PUSH_PROVIDERS.map((provider) => ({
                  label: providerLabel(provider),
                  value: provider
                }))
              "
            />
          </label>
        </div>

        <div class="form-row-2">
          <label class="form-field">
            <span class="field-label required">受众类型</span>
            <RrSelect
              v-model:value="form.audienceType"
              :options="AUDIENCE_TYPES.map((type) => ({ label: type, value: type }))"
            />
          </label>
          <label class="form-field">
            <span class="field-label">套用消息模板 (可选)</span>
            <RrSelect
              v-model:value="form.templateCode"
              :options="[
                { label: '不使用模板', value: '' },
                ...activeTemplates.map((template) => ({
                  label: `${template.templateName} (${template.templateCode})`,
                  value: template.templateCode
                }))
              ]"
            />
          </label>
        </div>

        <label class="form-field">
          <span class="field-label required">任务标题</span>
          <RrInput
            v-model:value.trim="form.title"
            placeholder="请输入推送标题"
            autocomplete="off"
          />
        </label>

        <label class="form-field">
          <span class="field-label required">任务内容</span>
          <RrInput
            v-model:value="form.body"
            type="textarea"
            :rows="3"
            placeholder="请输入推送内容..."
          />
        </label>
      </div>
    </AdminDialog>

    <AdminDialog
      v-model:open="detailVisible"
      title="任务详情"
      :width="720"
      :show-cancel="false"
      ok-text="关闭"
      :confirm-loading="detailLoading"
      @ok="detailVisible = false"
      @cancel="detailVisible = false"
    >
      <div v-if="taskDetail" class="detail-stack">
        <p>
          <strong>{{ taskDetail.task.title }}</strong>
          · {{ taskDetail.task.status }} · {{ taskDetail.task.appCode }}
        </p>
        <p class="text-time">
          成功 {{ taskDetail.task.successCount }} / 失败 {{ taskDetail.task.failureCount }} / 跳过
          {{ taskDetail.task.skippedCount }}
        </p>
        <p class="text-time">
          投递 {{ (taskDetail.deliveries || []).length }} 条，日志
          {{ (taskDetail.logs || []).length }} 条
        </p>
        <ul class="detail-list">
          <li v-for="delivery in (taskDetail.deliveries || []).slice(0, 20)" :key="delivery.id">
            #{{ delivery.id }} {{ delivery.provider }} {{ delivery.status }}
            <span v-if="delivery.errorCode">（{{ delivery.errorCode }}）</span>
          </li>
        </ul>
      </div>
      <p v-else-if="detailLoading" class="text-time">正在加载任务详情…</p>
    </AdminDialog>
  </div>
</template>

<style scoped>
.push-tab-pane {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
}

.text-time {
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text-muted);
}

.text-green {
  color: var(--rr-color-success);
  font-weight: var(--rr-font-weight-bold);
}
.text-red {
  color: var(--rr-color-danger);
  font-weight: var(--rr-font-weight-bold);
}

.modal-form-grid {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
  padding: var(--rr-space-2) 0;
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--rr-space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-2);
}

.field-label {
  font-size: var(--rr-font-size-base);
  font-weight: var(--rr-font-weight-bold);
  color: var(--rr-color-text);
}

.field-label.required::after {
  content: ' *';
  color: var(--rr-color-danger);
}

.row-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rr-space-2);
}

.detail-stack {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-2);
}

.detail-list {
  margin: 0;
  padding-left: var(--rr-space-5);
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-1);
  color: var(--rr-color-text);
  font-size: var(--rr-font-size-base);
}
</style>
